/**
 * useNectarisFx — React hook fiel ao nectaris-fx-v5.js original.
 * Implementa reveal / parallax / spin com cleanup completo no unmount.
 * Chamado UMA VEZ em SiteLayout (garante 1 instância, sem duplicar loops).
 *
 * Comportamentos fiéis ao arquivo-fonte (115 linhas):
 *  - reveal: data-reveal + data-reveal-delay → data-fx-in após delay
 *    IntersectionObserver threshold:0, rootMargin:'10000px 0px 0px 0px'
 *  - parallax: data-parallax (default 0.15), rAF throttled, passive scroll
 *  - spin: data-spin (default 0.05), data-spin-drift (default 0.0028),
 *    loop rAF; filhos data-spin-counter recebem rotate(-a)
 *    skip se |a-last|<0.5, fora do viewport, ou document.hidden
 *  - prefers-reduced-motion: revela tudo imediatamente, NÃO liga parallax/spin
 */

import { useEffect } from "react";

export function useNectarisFx() {
  useEffect(() => {
    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    /* -------------------------------------------------------
       REVEAL — IntersectionObserver fiel ao original
    ------------------------------------------------------- */
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const el = e.target as HTMLElement;
            const d = parseFloat(el.getAttribute("data-reveal-delay") ?? "0");
            setTimeout(() => { el.setAttribute("data-fx-in", ""); }, d);
            io.unobserve(el);
          }
        });
      },
      { threshold: 0, rootMargin: "10000px 0px 0px 0px" }
    );

    // Rescan periódico (1500ms) para detectar elementos montados por SPA
    let scanTimer: ReturnType<typeof setTimeout>;
    const rescan = () => {
      document
        .querySelectorAll<HTMLElement>("[data-reveal]")
        .forEach((el) => {
          if (reducedMotion) {
            // Revela imediatamente sem animação
            el.setAttribute("data-fx-in", "");
          } else if (
            !(el as HTMLElement & { __fx?: boolean }).__fx
          ) {
            (el as HTMLElement & { __fx?: boolean }).__fx = true;
            io.observe(el);
          }
        });
      scanTimer = setTimeout(rescan, 1500);
    };
    const firstScan = setTimeout(rescan, 60);

    // Se reduced-motion, não sobe parallax/spin — encerra aqui
    if (reducedMotion) {
      return () => {
        clearTimeout(firstScan);
        clearTimeout(scanTimer);
        io.disconnect();
      };
    }

    /* -------------------------------------------------------
       PARALLAX — rAF throttled, scroll passivo
    ------------------------------------------------------- */
    let pEls: HTMLElement[] = [];
    let ticking = false;

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const vh = innerHeight;
        pEls.forEach((el) => {
          const f = parseFloat(el.getAttribute("data-parallax") ?? "0.15");
          const r = el.getBoundingClientRect();
          if (r.bottom < -80 || r.top > vh + 80) return;
          el.style.transform =
            "translateY(" +
            ((r.top + r.height / 2 - vh / 2) * -f).toFixed(1) +
            "px)";
        });
        ticking = false;
      });
    };

    addEventListener("scroll", onScroll, { passive: true });

    // Rescan periódico para parallax (acoplado ao mesmo timer do reveal)
    const rescanParallax = () => {
      pEls = Array.from(
        document.querySelectorAll<HTMLElement>("[data-parallax]")
      );
    };
    rescanParallax();
    // Atualiza pEls a cada 1500ms junto com o reveal rescan
    // (o scanTimer já re-executa rescan que pode atualizar pEls)
    // Para garantir, sobrescrevemos rescan para também chamar rescanParallax
    // Porém já fazemos isso manualmente abaixo via o setInterval separado
    const parallaxTimer = setInterval(rescanParallax, 1500);

    /* -------------------------------------------------------
       SPIN — loop rAF contínuo
       a = scrollY * spin + t * spinDrift
       skip se |a - last| < 0.5, fora do viewport, document.hidden
    ------------------------------------------------------- */
    const spinLast = new WeakMap<HTMLElement, number>();
    let spinRaf: number;

    const spinLoop = (t: number) => {
      if (!document.hidden) {
        document
          .querySelectorAll<HTMLElement>("[data-spin]")
          .forEach((el) => {
            const r = el.getBoundingClientRect();
            if (r.bottom < -60 || r.top > innerHeight + 60) return;
            const a =
              (window.scrollY || 0) *
                parseFloat(el.getAttribute("data-spin") ?? "0.05") +
              t * parseFloat(el.getAttribute("data-spin-drift") ?? "0.0028");
            const last = spinLast.get(el);
            if (last !== undefined && Math.abs(a - last) < 0.5) return;
            spinLast.set(el, a);
            el.style.transform = "rotate(" + a.toFixed(2) + "deg)";
            el
              .querySelectorAll<HTMLElement>("[data-spin-counter]")
              .forEach((c) => {
                c.style.transform = "rotate(" + (-a).toFixed(2) + "deg)";
              });
          });
      }
      spinRaf = requestAnimationFrame(spinLoop);
    };
    spinRaf = requestAnimationFrame(spinLoop);

    /* -------------------------------------------------------
       CLEANUP
    ------------------------------------------------------- */
    return () => {
      clearTimeout(firstScan);
      clearTimeout(scanTimer);
      clearInterval(parallaxTimer);
      removeEventListener("scroll", onScroll);
      cancelAnimationFrame(spinRaf);
      io.disconnect();
    };
  }, []);
}
